// eslint-disable-next-line no-unused-vars
import React from 'react';
import { object, bool, func } from 'prop-types';
import styled from '@emotion/styled';
import {
  ComposedModal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
} from '@carbon/react';
import { AlertTriangle, Info, Package, Copy } from 'lucide-react';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';

import useToastNotifications from 'Hooks/useToastNotifications';
import formatWeeksAndDays from 'Utils/formatWeeksAndDays';
import copyToClipboard from 'Utils/copyToClipboard';

import Img from 'Containers/Image/Image';

const StyledComposedModal = styled(ComposedModal)`
  overflow: visible;
  z-index: 9001;
  
  .cds--modal-container {
    overflow: visible;
  }
`;

const StyledFormContainer = styled(Form)`
  padding: 0;

  .wrapper {
    padding: 1rem;
  }

  .preview {
    display: flex;

    .thumb {
      width: 130px;
      height: 130px;
      margin-right: 15px;
      text-align: center;

      .image {
        max-width: 100%;
        max-height: 100%;
        height: auto;
        width: 100%;
        margin: 0 auto;
      }
    }

    .details {
      width: 100%;

      .description {
        padding: 0;
        display: -webkit-box;
        -webkit-line-clamp: 4;
        -webkit-box-orient: vertical;
        overflow: hidden;
        margin-bottom: 2px;
        max-height: 80px;

        p {
          font-size: 0.875rem;
          color: ${ props => props.theme.colors.darkGray };
        }
      }

      .code {
        font-size: 0.875rem;
        font-weight: 600;
        span {
          font-weight: normal;
          margin: 0 3px;

          svg {
            cursor: pointer;
            position: relative;
            top: 3px;
            left: 2px;
          }
        }
      }
    }
  }

  .heading {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 15px 0 20px;
    font-weight: 600;

    svg {
      min-width: 30px;
      margin-right: 5px;
      fill: #FB7501;
    }

    .warning2 {
      display: block;
      margin-top: 8px;
      font-weight: normal;
      font-size: 0.8125rem;
      color: #FB7501;
    }
  }

  strong {
    font-weight: 600;
  }

  .cds--modal-footer {
    margin: 0;

    button {
      flex: 1;
    }
  }
`;

const StyledModalBody = styled(ModalBody)`
  overflow: visible;
  background-color: ${ props => props.theme.colors.white };
  color: ${ props => props.theme.colors.darkGray };
  margin-bottom: 0;
  padding: 0 !important;
  font-size: 0.875rem;

  .cds--form-item {
    margin-bottom: 10px !important;
  }
`;

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  max-width: none !important;
  padding: 0 !important;
`;

const ExternalSupplierModal = ({
  open,
  product,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const toast = useToastNotifications();

  const copyCodeToClipboard = async code => {
    const resp = await copyToClipboard(code);

    if (resp) {
      toast.addNotification({
        type: 'success',
        title: t('products.copyCodeSuccess')
      });
    }
  };

  return (
    <StyledComposedModal
      open={ open }
      onClose={ onClose }
      size="sm"
      selectorPrimaryFocus=".cds--modal-content"
    >
      <ModalHeader>
        <h4>{ t('products.confirmExternalSupplierProduct') }</h4>
      </ModalHeader>
      <StyledModalBody theme={ theme }>
        <StyledFormContainer
          theme={ theme }
          onSubmit={ onSubmit }
        >
          <div className="wrapper">
            <div className="preview">
              <div className="thumb">
                <Img
                  className="image"
                  src={ [
                    product?.picture
                    || product?.pictures?.at(0)?.urlThumbnailPicture
                  ] }
                  unloader={ <Package size={ 50 } strokeWidth={ 1.25 } /> }
                  alt={ product.name }
                />
              </div>
              <div className="details">
                <div className="description"><p>{ product.description || product.title || product.name }</p></div>
                <p className="code">
                  { t('products.code') }:
                  <span>{ product.code }</span>
                  <span><Copy size={ 18 } role="button" className="copy" onClick={ () => copyCodeToClipboard(product?.code || '') } /></span>
                </p>
              </div>
            </div>
            <div className="heading">
              <AlertTriangle size={ 20 } />
              <span>
                { t('products.confirmExternalSupplierWarning') }
                <br />
                <span className="warning2">{ t('products.confirmExternalSupplierWarning2') }</span>
              </span>
            </div>
            { product?.supplierId === 1 && (
              <div className="heading">
                <Info size={ 20 } />
                <span>{ t('products.confirmExternalSupplierInfo') }</span>
              </div>
            )}
            <span
              dangerouslySetInnerHTML={ {
                __html: t('products.confirmExternalSupplierMessage', { estimate: formatWeeksAndDays(product.deliveryTimeDays || product.supplierOfferDeliveryTimeDays) })
              } }
            />
          </div>
          <ModalFooter>
            <StyledButton
              className="red submit"
              type="submit"
              kind="primary"
              data-test="onAddSupplierProductConfirm"
            >
              { t('common.agree') }
            </StyledButton>
          </ModalFooter>
        </StyledFormContainer>
      </StyledModalBody>
    </StyledComposedModal>
  );
};

ExternalSupplierModal.propTypes = {
  open: bool,
  product: object,
  onClose: func,
  onSubmit: func,
};

export default ExternalSupplierModal;
